import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiRefusal } from '@gatecheckpro/api-client';
import { Button, Card, Field, Notice } from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { FailureNotice } from '../lib/refusal.tsx';
import { startCabsSignIn } from '../lib/cabs.ts';

/**
 * Sign in.
 *
 * The second factor is a *second step*. The server answers `AUTH_MFA_REQUIRED`
 * only once the password is known-good, so the form never reveals that an
 * account has MFA to someone who has not proved the password.
 */
export function SignInPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  /*
   * Where to go afterwards, when somebody was sent here mid-task.
   *
   * Only a path within this app. A `next` that could name another origin is an
   * open redirect — the classic form of it is a phishing link that signs a
   * person in legitimately and then lands them somewhere that is not us — so
   * anything not starting with a single slash is ignored rather than sanitised.
   */
  const requested = search.get('next');
  const next =
    requested !== null && requested.startsWith('/') && !requested.startsWith('//')
      ? requested
      : '/tickets';
  const { reload } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [needsSecondFactor, setNeedsSecondFactor] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [cabsBusy, setCabsBusy] = useState(false);

  /*
   * The CABS route is its own action, not this form.
   *
   * A CABS identity has no password here and never will: typing one into the fields above cannot
   * work, and offering only this form to somebody who has a CABS account is how they conclude the
   * product is broken. The button leaves for CABS Identity, which is the only place that
   * authenticates them.
   */
  async function signInWithCabs(): Promise<void> {
    setCabsBusy(true);
    setError(null);
    try {
      await startCabsSignIn(next);
    } catch (caught) {
      setError(caught);
      setCabsBusy(false);
    }
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await api.request<{
        accessToken: string;
        accessTokenExpiresAt: string;
        sessionToken: string;
        sessionExpiresAt: string;
        mfaSatisfied: boolean;
      }>('/api/v1/auth/login', {
        method: 'POST',
        anonymous: true,
        body: {
          email,
          password,
          ...(needsSecondFactor && mfaCode.length > 0 ? { mfaCode } : {}),
          deviceName: 'GateCheck web',
        },
      });
      api.setSession(session);
      await reload();
      void navigate(next, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiRefusal && caught.code === 'AUTH_MFA_REQUIRED') {
        setNeedsSecondFactor(true);
        setError(null);
      } else {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="gc-auth">
      <div className="gc-auth__card gc-stack">
        <div className="gc-stack gc-stack--tight">
          <span className="gc-wordmark">
            Gate<span className="gc-wordmark__accent">Check</span>
          </span>
          <p className="gc-lede">Your tickets, wherever you bought them.</p>
        </div>

        <Card>
          <form className="gc-stack" onSubmit={(event) => void submit(event)}>
            <h1>Sign in</h1>

            <FailureNotice error={error} />

            {needsSecondFactor && (
              <Notice tone="info" title="One more step">
                Enter the 6-digit code from your authenticator app, or a recovery code.
              </Notice>
            )}

            <Field
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Field
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />

            {needsSecondFactor && (
              <Field
                label="Authentication code"
                name="mfaCode"
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                hint="6 digits from your app, or one of your recovery codes."
                value={mfaCode}
                onChange={(event) => setMfaCode(event.target.value)}
              />
            )}

            <Button type="submit" variant="primary" size="large" block busy={busy}>
              {needsSecondFactor ? 'Verify and sign in' : 'Sign in'}
            </Button>
          </form>
        </Card>

        <Card>
          <div className="gc-stack gc-stack--tight">
            <h2>Sign in with CABS</h2>
            <p className="gc-muted">
              If you use a CABS identity — with a passkey, or a phone — sign in with it here rather
              than with a password.
            </p>
            <Button
              type="button"
              variant="secondary"
              size="large"
              block
              busy={cabsBusy}
              onClick={() => void signInWithCabs()}
            >
              Sign in with CABS
            </Button>
            <p className="gc-muted">
              First time with CABS?{' '}
              <Link to="/auth/cabs/first-use">Set up your GateCheck account</Link>
            </p>
          </div>
        </Card>

        <p className="gc-muted">
          New here? <Link to="/register">Create an account</Link>
        </p>
      </div>
    </main>
  );
}
